import type { CustomerClassification } from '@/utils/customerClassification';
import { zafClient } from './zafClient';

export interface ZendeskItem {
    id: number;
    name: string;
    active?: boolean;
}

export interface CurrentTicket {
    id: number | null;
    type: "problem" | "incident" | "question" | "task" | null;
}

export interface ZafRequestOptions {
    url: string;
    type?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    contentType?: string;
    data?: string;
    [key: string]: unknown;
}

export interface UserField {
    id: number;
    key: string;
    title: string;
    active: boolean;
    type?: string;
    custom_field_options?: { id: number; name: string; value: string }[];
    system_field_options?: { name: string; value: string }[];
}

export interface TicketField {
    id: number;
    type: string;
    title: string;
    active: boolean;
    custom_field_options?: { id: number; name: string; value: string }[];
    system_field_options?: { name: string; value: string }[];
}

export async function zendeskRequest<T>(options: ZafRequestOptions): Promise<T> {
    if (!zafClient) throw new Error("zafClient não inicializado");
    return await zafClient.request(options) as T;
}

export async function getCurrentTicket(): Promise<CurrentTicket> {
    if (!zafClient) throw new Error("zafClient não inicializado");

    const result = await zafClient.get(["ticket.id", "ticket.type"]) as Record<string, unknown>;

    return {
        id: result["ticket.id"] ? Number(result["ticket.id"]) : null,
        type: (result["ticket.type"] as CurrentTicket["type"]) || null,
    };
}

interface TicketResponse {
    ticket: {
        id: number;
        type: string | null;
        problem_id: number | null;
    };
}

export const getTicketById = (id: number) =>
    zendeskRequest<TicketResponse>({ url: `/api/v2/tickets/${id}.json` });

export const getBrands = () => zendeskRequest<{ brands: ZendeskItem[] }>({ url: '/api/v2/brands.json' });
export const getGroups = () => zendeskRequest<{ groups: ZendeskItem[] }>({ url: '/api/v2/groups.json' });
export const getTicketForms = () => zendeskRequest<{ ticket_forms: ZendeskItem[] }>({ url: '/api/v2/ticket_forms.json' });
export const getZendeskUser = () => zendeskRequest<{ users: ZendeskItem[] }>({ url: '/api/v2/users.json' });

export const promoteToProblem = async (ticketId: number) => {
    return zendeskRequest({
        url: `/api/v2/tickets/${ticketId}.json`,
        type: "PUT",
        contentType: "application/json",
        data: JSON.stringify({
            ticket: { type: "problem" },
        }),
    });
};

export const createTicket = (ticket: Record<string, unknown>) =>
    zendeskRequest({
        url: '/api/v2/tickets.json',
        type: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({ ticket })
    });

export async function getTicketFormById(formId: number) {
    return await zafClient?.request({
        url: `/api/v2/ticket_forms/${formId}.json`,
        type: 'GET'
    });
}

export const getTicketFields = () =>
    zendeskRequest<{ ticket_fields: TicketField[] }>({ url: '/api/v2/ticket_fields.json' });

export async function getTicketFieldValueByTitle(title: string): Promise<string | null> {
    if (!zafClient) return null

    const { ticket_fields } = await getTicketFields()
    const field = ticket_fields.find(f => f.title.toLowerCase() === title.toLowerCase())
    if (!field) return null

    const key = `ticket.customField:custom_field_${field.id}`
    const result = await zafClient.get(key) as Record<string, unknown>
    const value = result[key]
    return typeof value === 'string' ? value : null
}

export async function getCarrierFieldData(): Promise<{ fieldId: number; value: string | null } | null> {
    if (!zafClient) return null

    // 1. ticket ID do ticket aberto na sidebar
    const ticketData = await zafClient.get('ticket.id') as Record<string, unknown>
    const ticketId = ticketData['ticket.id'] as number | undefined
    if (!ticketId) return null

    // 2. campos do formulário → achar ID do campo "Transportadora"
    const fieldsResponse = await zafClient.request({
        url: '/api/v2/ticket_fields.json',
        type: 'GET',
        dataType: 'json',
    }) as { ticket_fields: TicketField[] }

    const field = fieldsResponse.ticket_fields.find(
        f => f.title.toLowerCase() === 'transportadora'
    )
    if (!field) return null

    // 3. ticket completo via REST → custom_fields tem todos os valores
    const ticketResponse = await zafClient.request({
        url: `/api/v2/tickets/${ticketId}.json`,
        type: 'GET',
        dataType: 'json',
    }) as { ticket: { custom_fields: Array<{ id: number; value: string | null }> } }

    const customField = ticketResponse.ticket.custom_fields.find(cf => cf.id === field.id)

    return {
        fieldId: field.id,
        value: typeof customField?.value === 'string' ? customField.value : null,
    }
}

export const getUserFields = () =>
    zendeskRequest<{ user_fields: UserField[] }>({ url: '/api/v2/user_fields.json' });

type AppBrand = 'Mizuno' | 'Olympikus' | 'Under Armour';

const BRAND_TO_FIELD_KEY: Record<AppBrand, string> = {
    'Mizuno': 'classificacao_mizuno',
    'Olympikus': 'classificacao_olympikus',
    'Under Armour': 'classificacao_underarmour',
};
const CLASSIFICATION_HIERARCHY: Record<CustomerClassification, number> = {
  'Bronze': 1,
  'Prata': 2,
  'Ouro': 3,
}
export const fillRequesterClassification = async (
  requesterId: number,
  medal: string,
  brand: string | null | undefined
) => {
  try {
    const normalizedBrand = (brand || '').trim() as AppBrand
    const fieldKey = BRAND_TO_FIELD_KEY[normalizedBrand]

    if (!fieldKey) {
      console.warn('Marca não reconhecida:', normalizedBrand)
      return
    }

    const fields = await getUserFields()
    const classificationField = fields.user_fields.find(
      (f: { key: string }) => f.key === fieldKey
    )

    if (!classificationField) {
      console.warn('Campo não encontrado:', fieldKey)
      return
    }

    const options = classificationField.custom_field_options
    if (!options?.length) return

    const matchedOption = options.find(
      opt => opt.name.replace(/^[^\p{L}]+/u, '').trim().toLowerCase() === medal.toLowerCase()
    )

    if (!matchedOption) {
      console.warn(`Opção "${medal}" não encontrada.`)
      return
    }

    const userResponse = await zendeskRequest<{ user: { user_fields: Record<string, string | null> } }>({
      url: `/api/v2/users/${requesterId}.json`,
      method: 'GET',
    })
    const currentValue = userResponse?.user?.user_fields?.[fieldKey]

    const currentOption = options.find(opt => opt.value === currentValue)
    const currentMedalName = currentOption
      ? currentOption.name.replace(/^[^\p{L}]+/u, '').trim()
      : null

    if (currentMedalName) {
      const currentRank = CLASSIFICATION_HIERARCHY[currentMedalName as CustomerClassification] ?? 0
      const newRank = CLASSIFICATION_HIERARCHY[medal as CustomerClassification] ?? 0

      if (newRank < currentRank) {
        popupError(`Classificação não pode ser rebaixada: classificação atual "${currentMedalName}"`, true)
        return { updated: false, reason: 'downgrade_blocked' }
      }
    }

    await zendeskRequest({
      url: `/api/v2/users/${requesterId}.json`,
      method: 'PUT',
      data: JSON.stringify({
        user: { user_fields: { [fieldKey]: matchedOption.value } },
      }),
      contentType: 'application/json',
    })
    return { updated: true }
  } catch (error) {
    console.error(`Erro ao preencher classificação: ${error}`, true)
    return { updated: false, reason: 'error' }
  }
}

export function popupError(string: string, sticky: boolean) {
  zafClient?.invoke("notify", string, "error", { sticky });
}

export function popupSuccess(string: string, sticky: boolean) {
  zafClient?.invoke("notify", string, "success", { sticky });
}